import { createScrollGuardStore } from "@ext/agent/components/hooks/scrollGuardStore";
import { useChatScroller } from "@ext/agent/components/hooks/useChatScroller";
import { useSessionScrollPosition } from "@ext/agent/components/hooks/useSessionScrollPosition";
import { useStickToBottom } from "@ext/agent/components/hooks/useStickToBottom";
import type { ChatMessage } from "@ext/agent/components/types/chat";
import { type ChatScrollPosition, saveChatScrollPosition } from "@ext/agent/components/utils/chatScrollMemory";
import { useCallback, useMemo, useRef } from "react";

const findLastUserMessageId = (messages: ChatMessage[]): string | null => {
	for (let i = messages.length - 1; i >= 0; i--) {
		if (messages[i].kind === "user") return messages[i].id;
	}
	return null;
};

export const useChatScroll = (messages: ChatMessage[], isOpen: boolean, sessionId: string | null) => {
	const innerContentRef = useRef<HTMLDivElement>(null);
	const scrollGuardStore = useMemo(() => createScrollGuardStore(), []);
	const lastUserMessageId = useMemo(() => findLastUserMessageId(messages), [messages]);

	const positionKey = isOpen && lastUserMessageId ? sessionId : null;
	const savePosition = useCallback(
		(position: ChatScrollPosition) => {
			if (positionKey) saveChatScrollPosition(positionKey, position);
		},
		[positionKey],
	);

	const { scroller, isAtBottom } = useChatScroller(savePosition);
	useSessionScrollPosition(scroller, sessionId, lastUserMessageId !== null, isOpen);
	useStickToBottom(scroller, innerContentRef, scrollGuardStore, sessionId, lastUserMessageId);

	const scrollToBottom = useCallback(() => scroller.scrollToEnd("smooth"), [scroller]);

	return { scrollContainerRef: scroller.rootRef, innerContentRef, scrollGuardStore, isAtBottom, scrollToBottom };
};
